import CustomChartCard from "@/components/mocks/components/CustomChartCard";
import { Colors } from "@/enums/Colors";
import User from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/User";
import { Chart } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/charts/Chart";
import StringUtils from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/StringUtils";
import { FinancialStatementItem } from "@/models/accounting/ReportItem";
import { ChatMessageFragment, ChatMessageFragmentType } from "@/models/ai-chat/ChatMessageFragment";
import { LazoChatConversation } from "@/models/ai-chat/LazoChatConversation";
import { LazoChatMessage, Metadata } from "@/models/ai-chat/LazoChatMessage";
import { Funder, FunderType } from "@/types/FunderTypes";
import { MetadataType } from "@/types/MetadataType";
import { US_DOLLAR_FORMATTER_NO_DECIMALS } from "@/utils/CurrencyFormat";
import { DATE_GROUPS, getElementDateGroup } from "@/utils/DateGroup";
import { Box, Typography } from "@mui/material";
import React, { LegacyRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageFunder } from "../AddInvestorToFunderListModal";
import ChatAvatar from "../ChatAvatar";
import CompanyTemplate from "../funder-templates/CompanyTemplate";
import InvestorTemplate from "../funder-templates/InvestorTemplate";
import PersonTemplate from "../funder-templates/PersonTemplate";
import ChatMessageContainer, { BotMessageButtons, ChatMessageProps } from "./ChatMessageContainer";
import { LazoChatMessageComponent } from "./LazoChatMessageComponent";

type Props = ChatMessageProps & {
  lazoChatConversation?: LazoChatConversation;
  isLastMessage?: boolean;
  messageEntity: LazoChatMessage;
  explainClick: () => void;
  approachEmailClick: () => void;
  followUpEmailClick: () => void;
};

const isExportableMessage = (metadata: any) => {
  if (!metadata) {
    return;
  }
  let message: Metadata;
  if (typeof metadata === "string") {
    message = JSON.parse(metadata);
  } else {
    message = metadata;
  }

  if (message.metadataType === MetadataType.FIND_INVESTORS) {
    const investors: MessageFunder[] = message.investors as MessageFunder[];
    return investors.length > 0;
  }

  if (message.metadataType === MetadataType.FIND_INVESTOR) {
    const investors: MessageFunder[] = message.investors as MessageFunder[];
    return investors.length > 0;
  }
};

const isMailableMessage = (message: LazoChatMessage) => {
  try {
    let parseableMessage = JSON.parse(message.message).type;
    if (parseableMessage != undefined && parseableMessage != null) {
      return [ChatMessageFragmentType.FUNDER].includes(parseableMessage);
    }
  } catch (e) {
    return false;
  }
  return false;
};

export const ChatbotMessage = React.forwardRef(function ChatbotMessage(props: Props, ref: LegacyRef<HTMLDivElement>) {
  const { t } = useTranslation("chat");
  const [openAddToFunderListModal, setOpenAddToFunderListModal] = useState(false);
  const [isSystemMessage, setIsSystemMessage] = useState<boolean>(true);
  const [dateGroup, setDateGroup] = useState<string>("");

  // This is needed for now for backwards compatiblity, to avoid breaking the UI when the msgs are not fragments.
  let fragment: ChatMessageFragment;

  const isExportable = isExportableMessage(props.messageEntity.metadata);
  const isMailable = isMailableMessage(props.messageEntity);

  try {
    if (props.messageEntity.messageFragments && props.messageEntity.messageFragments.length > 0) {
      // TODO: Handle multiple message fragments
      fragment = props.messageEntity.messageFragments![0];
    } else {
      fragment = JSON.parse(props.messageEntity.message) as ChatMessageFragment;
    }
  } catch (e) {
    fragment = {
      type: ChatMessageFragmentType.STRING,
      content: props.messageEntity.message,
    };
  }

  const isBotMessage = !props.messageSender;

  useEffect(() => {
    if (isBotMessage && props.isLastMessage) {
      setDateGroup(getElementDateGroup(props.messageEntity));
    }
    setIsSystemMessage([ChatMessageFragmentType.INVITED_USERS_ARRAY, ChatMessageFragmentType.REMOVED_USERS_ARRAY].includes(fragment.type));
  }, [fragment]);

  return (
    <span ref={ref}>
      {isBotMessage && !isSystemMessage && (
        <ChatAvatar
          userAvatarStyle={{
            justifyContent: "end",
            paddingRight: "0.875rem",
          }}
          isBotMessage={isBotMessage}
          user={props.messageSender || ({ firstName: "No user", lastName: "" } as User)}
        />
      )}
      {!isSystemMessage ? (
        <>
          <ChatMessageContainer {...props}>
            {renderFragmentByType(fragment, props.messageEntity, t)}
            {isBotMessage && dateGroup === DATE_GROUPS.TODAY && props.isLastMessage ? (
              <BotMessageButtons
                lazoChatConversation={props.lazoChatConversation}
                messageEntity={props.messageEntity}
                isExportable={isExportable}
                openAddToFunderListModal={openAddToFunderListModal}
                setOpenAddToFunderListModal={setOpenAddToFunderListModal}
                explainClick={props.explainClick}
                writeApproachEmailClick={isMailable ? props.approachEmailClick : undefined}
                writeFollowUpEmailClick={isMailable ? props.followUpEmailClick : undefined}
              />
            ) : (
              <></>
            )}
          </ChatMessageContainer>
        </>
      ) : (
        renderFragmentByType(fragment, props.messageEntity, t, props.lazoUser.user)
      )}
    </span>
  );
});

ChatbotMessage.displayName = "ChatbotMessage";

export const renderFragmentByType = (
  fragment: ChatMessageFragment,
  messageEntity: LazoChatMessage,
  t: (key: string) => string,
  currentUser?: User
) => {
  let content = null;
  switch (fragment.type) {
    case ChatMessageFragmentType.STRING: {
      content = fragment.content as string;
      //return <div style={{ whiteSpace: "pre-line"}} >{content}</div>
      return (
        <LazoChatMessageComponent
          message={content}
          metadata={messageEntity.metadata}
          isBotMessage={!!messageEntity.isBotMessage}
          isBotTheMessageReceiver={!!messageEntity.messageReceiverIsBot}
        />
      );
    }
    case ChatMessageFragmentType.CHART: {
      content = fragment.content as Chart;
      return handleChart(content, t);
    }
    case ChatMessageFragmentType.HTML: {
      return <Box dangerouslySetInnerHTML={{ __html: fragment.content as string }} />;
    }
    case ChatMessageFragmentType.INVITED_USERS_ARRAY: {
      content = fragment.content as string[];
      let displayString = `<span style='font-weight: 400; color: ${Colors.BLACK_151517}; line-height: 18px;'>${t("chatbotMessage.systemMessages.youInvited")}&nbsp;</span>`;
      if (content.includes(currentUser?.firstName + " " + currentUser?.lastName)) {
        displayString = t("chatbotMessage.systemMessages.youWereAdded");
      } else {
        content.forEach((name, idx) => {
          displayString += `<span style='font-weight: 500; color: ${Colors.BLACK_151517}; line-height: 18px;'>${name}</span>`;
          if (idx !== content.length - 1) {
            displayString += ",&nbsp";
          }
        });
      }
      return (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              padding: "1rem 0",
            }}
            dangerouslySetInnerHTML={{ __html: displayString }}
          />
        </>
      );
    }
    case ChatMessageFragmentType.REMOVED_USERS_ARRAY: {
      content = fragment.content as string[];
      let displayString = `${t("chatbotMessage.systemMessages.youRemoved")}&nbsp;`;
      if (content.includes(currentUser?.firstName + " " + currentUser?.lastName)) {
        displayString = t("chatbotMessage.systemMessages.youWereRemoved");
      } else {
        content.forEach((name, idx) => {
          displayString += `<b>${name}</b>`;
          if (idx !== content.length - 1) {
            displayString += ",&nbsp";
          }
        });
      }
      return (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              padding: "1rem 0",
            }}
            dangerouslySetInnerHTML={{ __html: displayString }}
          />
        </>
      );
    }
    case ChatMessageFragmentType.FINANCIAL_STATEMENT_ITEM: {
      const item = fragment.content as FinancialStatementItem;
      return (
        <Box sx={{ padding: "1rem" }}>
          <Typography>
            {" "}
            {t("chatbotMessage.questions.financialStatement")} {US_DOLLAR_FORMATTER_NO_DECIMALS.format(item.value)} <b>{item.name}</b>{" "}
            {t("chatbotMessage.questions.financialStatementItemFrom")} {item.date}?
          </Typography>
        </Box>
      );
    }
    case ChatMessageFragmentType.FUNDER: {
      const funder: Funder = fragment.content as Funder;
      const funderType = funder.type;
      if (funderType == FunderType.PERSON || !funderType) {
        return <PersonTemplate funder={funder} />;
      }
      if (funderType === FunderType.ORGANIZATION) {
        return <CompanyTemplate funder={funder} />;
      }
      if (funderType == FunderType.INVESTOR) {
        return <InvestorTemplate funder={funder} />;
      }
    }
    case ChatMessageFragmentType.ROOM_DOCUMENT: {
      return (
        <LazoChatMessageComponent
          message={t("chatbotMessage.questions.document")}
          isBotMessage={!!messageEntity.isBotMessage}
          isBotTheMessageReceiver={!!messageEntity.messageReceiverIsBot}
          metadata={messageEntity.metadata}
        />
      );
    }
    case ChatMessageFragmentType.SHARED_FINANCIALS: {
      return (
        <LazoChatMessageComponent
          message={t("chatbotMessage.questions.financialStatements")}
          isBotMessage={!!messageEntity.isBotMessage}
          isBotTheMessageReceiver={!!messageEntity.messageReceiverIsBot}
          metadata={messageEntity.metadata}
        />
      );
    }
    default: {
      return <></>;
    }
  }
};

const handleChart = (content: Chart, t: (key: string) => string) => {
  return (
    <>
      <Box sx={{ padding: "1rem 0 0.5rem 0" }}>
        <CustomChartCard chart={content} />
        <Typography sx={{ paddingTop: "0.5rem" }}>
          {t("chatbotMessage.questions.chartPrefix")} <strong>{StringUtils.capitalizeWords(content.title.replaceAll("_", " "))}</strong>{" "}
          {t("chatbotMessage.questions.chartSuffix")}
        </Typography>
      </Box>
    </>
  );
};
